/* Common Imports */

import { styled, useMediaQuery } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "bson";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../models";
import StateInterface from "../../../../redux-magic/state-interface";
import { PropertySnackbarInterface } from "../../../../redux-magic/sub-interfaces/property-snackbar";

/* Component Imports */

import { AddPhotoAlternateOutlined } from "@mui/icons-material";
import { Skeleton, SvgIcon, Typography, useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { LoginFlowTriggers, createLoginFlow } from "../../../../lib/auth/helpers";
import RequestImagesIcon from "../../../../public/images/icons/singleproperty/request_image.svg";
import transaction_type from "../../../../redux-magic/sub-state-data-empty/transaction-tabs";
import { StoreLeadsThunk } from "../../../../redux-magic/thunks";
import BeegruButton from "../../../common-components/buttons/BeegruButton";
import UploadImagesMobile from "../../../modals/PropertyModal/UploadPropertyImagesMobile";
import UploadImageModal from "../../../modals/PropertyModal/UploadPropertyImagesModal";
import NewPropertyImageComponent from "./CarouselContent/NewPropertyImageComponent";
import LeftContainer from "./LeftContent/LeftContainer";
import RightContainer from "./RightContent/RightContainer";

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

/* Styled Components */

const Container = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row-reverse",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "1rem",
	margin: "1rem 0rem 1.5rem 0rem",
	[theme.breakpoints.down("md1190")]: {
		gap: "1rem",
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		width: "100%",
		margin: "0rem 0rem 0rem 0rem",
	},
}));

const ContainerOne = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "flex-start",
	width: "25%",
	gap: "1rem",
	top: "5rem",
	position: "sticky",
	zIndex: 4,
	[theme.breakpoints.down("md1190")]: {
		display: "none",
	},
}));

const ContainerTwo = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "75%",
	gap: "1rem",
	[theme.breakpoints.down("md1190")]: {
		width: "100%",
		minWidth: "100%",
		maxWidth: "100%",
	},
	[theme.breakpoints.down("sm")]: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		padding: "0rem 0rem 0.5rem 0rem",
		gap: "0.5rem",
	},
}));

const ImageContainer = styled("div")(({ theme }) => ({
	width: "100%",
	height: "100%",
	borderRadius: "16px",
	cursor: "pointer",

	[theme.breakpoints.down("md1190")]: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
	},
}));

const ComponentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "calc(100% - 5rem)",
	border: "2px dashed #EE4B2B",
	borderRadius: "16px",
	borderColor: theme.palette.primary.main,
	padding: "2rem",
	gap: "0.25rem",
}));

const NoImgContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	[theme.breakpoints.down("sm")]: {
		margin: "1.5rem 0rem 0rem 0rem",
	},
}));

const DefaultImage = styled("img")(({ theme }) => ({
	width: "100%",
	borderRadius: "16px",
	objectFit: "cover",
	maxHeight: "39rem",
	backgroundColor: theme.palette.background.paper,
	[theme.breakpoints.down(theme.breakpoints.values.xxxl + 10)]: {
		height: "735px",
	},
	[theme.breakpoints.down("xxxl")]: {
		height: "650px",
	},
	[theme.breakpoints.down("xxl")]: {
		height: "523px",
	},
	[theme.breakpoints.down("xl")]: {
		height: "572px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.lgPlus + 100)]: {
		height: "570px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.lgPlus + 10)]: {
		height: "535px",
	},
	[theme.breakpoints.down("lgPlus")]: {
		height: "504px",
	},
	[theme.breakpoints.down("lg")]: {
		height: "605px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.md1220 + 10)]: {
		height: "615px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.md1190 + 11)]: {
		height: "598px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.md + 33)]: {
		height: "486px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.md + 10)]: {
		height: "486px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.md + 5)]: {
		height: "469px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.sm910 + 1)]: {
		height: "459px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.sm860 + 1)]: {
		height: "430px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.sm822 + 1)]: {
		height: "409px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.smPlus + 1)]: {
		height: "378px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.sm + 1)]: {
		height: "284px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.xsPlus + 40)]: {
		height: "297px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.xsPlus + 10)]: {
		height: "277px",
	},
	[theme.breakpoints.down(theme.breakpoints.values.xs + 1)]: {
		height: "149px",
	},
	[theme.breakpoints.down("sm")]: {
		display: "flex",
		flexDirection: "column",
		justifyContent: "center",
		alignItems: "center",
		height: "14.5rem",
	},
}));

const LoadingContainer = styled("div")(({ theme }) => ({
	height: "30rem",
	width: "100%",
	[theme.breakpoints.down("sm")]: {
		height: "15rem",
	},
}));

const SectionTwo = ({
	session,
	redux_session_updated,
	propertyData,
	dispatch,
	location,
	profile_context,
	isEditor,
	section_to_edit,
	data_before_edit,
	initialLoad,
	loading_property_details,
	loading_property_config_and_area,
	loading_about_property,
	loading_overview,
	loading_amenity,
	loading_about_developer,
	loading_about_location,
	loading_reviews,
	loading_vicinity,
	loading_neighbourhood_reviews,
	loading_property_creator,
	loading_map,
	property_detailsInnerRef,
	overviewInnerRef,
	aboutPropertyInnerRef,
	amenitiesInnerRef,
	aboutDeveloperInnerRef,
	aboutLocationInnerRef,
	locationCoordinatesInnerRef,
	vicinityInnerRef,
	neighbourhoodReviewsInnerRef,
	reviewsInnerRef,
	creatorInnerRef,
	property_snackbar,
	propertyDetails_inView,
	singlePropertyOverview_inView,
	aboutSingleProperty_inView,
	singlePropertyAmenities_inView,
	singlePropertyDeveloper_inView,
	singlePropertyAboutLocation_inView,
	singlePropertyReviews_inView,
	singlePropertyLocationCoordinates_inView,
	singlePropertyVicinity_inView,
	is_user_creator,
	logged_user_access,
	logged_user,
	saved_content,
	loading_property_images,
	login_flow_global_state,
}: {
	session: any;
	redux_session_updated: boolean;
	propertyData: StateInterface["single_property_page"];
	dispatch: Function;
	location: StateInterface["location"];
	profile_context: StateInterface["profile_context"];
	isEditor: boolean;
	section_to_edit: string;
	data_before_edit: any;
	initialLoad: boolean;
	loading_property_details: boolean;
	loading_property_config_and_area: boolean;
	loading_about_property: boolean;
	loading_overview: boolean;
	loading_amenity: boolean;
	loading_about_developer: boolean;
	loading_about_location: boolean;
	loading_reviews: boolean;
	loading_vicinity: boolean;
	loading_neighbourhood_reviews: boolean;
	loading_property_creator: boolean;
	loading_map: boolean;
	property_detailsInnerRef: any;
	overviewInnerRef: any;
	aboutPropertyInnerRef: any;
	amenitiesInnerRef: any;
	aboutDeveloperInnerRef: any;
	aboutLocationInnerRef: any;
	locationCoordinatesInnerRef: any;
	vicinityInnerRef: any;
	neighbourhoodReviewsInnerRef: any;
	reviewsInnerRef: any;
	creatorInnerRef: any;
	property_snackbar: PropertySnackbarInterface;
	propertyDetails_inView: boolean;
	singlePropertyOverview_inView: boolean;
	aboutSingleProperty_inView: boolean;
	singlePropertyAmenities_inView: boolean;
	singlePropertyDeveloper_inView: boolean;
	singlePropertyAboutLocation_inView: boolean;
	singlePropertyReviews_inView: boolean;
	singlePropertyLocationCoordinates_inView: boolean;
	singlePropertyVicinity_inView: boolean;
	is_user_creator: boolean;
	logged_user_access: string;
	logged_user: string | ObjectId;
	saved_content: LocalPropertyInterface["saved_content"];
	loading_property_images: boolean;
	login_flow_global_state: StateInterface["login_flow_global_state"];
}) => {
	const theme = useTheme();

	const router = React.useRef(useRouter()).current;

	const [scrollY, setScrollY] = React.useState<number>(0);

	const [negativeScroll, setNegativeScroll] = React.useState<boolean>(true);

	const [openProfileModal, setOpenProfileModal] = React.useState(false);

	const [editProfileModal, setEditProfileModal] = React.useState(false);

	const belowMd1190Device = useMediaQuery(theme.breakpoints.down("md1190"));

	const smDevice = useMediaQuery(theme.breakpoints.down("sm"));

	const [snackopen, setSnackOpen] = React.useState<boolean>(false);

	const localLang = {
		upload_images:
			router.locale === "kn-IN"
				? "ಚಿತ್ರಗಳನ್ನು ಅಪ್ಲೋಡ್ ಮಾಡಿ"
				: router.locale === "ar-AE"
					? "رفع الصور"
					: "Upload images",
		edit_images:
			router.locale === "kn-IN" ? "ಚಿತ್ರಗಳನ್ನು ಸಂಪಾದಿಸಿ" : router.locale === "ar-AE" ? "تعديل الصور" : "Edit images",
		delete_property:
			router.locale === "kn-IN" ? "ಸ್ವೀಕರಿಸಿ" : router.locale === "ar-AE" ? "حذف العقار" : "Delete property",
		save: router.locale === "kn-IN" ? "ಉಳಿಸಿ" : router.locale === "ar-AE" ? "حفظ" : "Save",
		share: router.locale === "kn-IN" ? "ಹಂಚಿಕೊಳ್ಳಿ" : router.locale === "ar-AE" ? "شارك" : "Share",
		report: router.locale === "kn-IN" ? "ವರದಿ" : router.locale === "ar-AE" ? "أبلغ" : "Report",
		add_property_pictures:
			router.locale === "kn-IN"
				? "ಚಿತ್ರಗಳನ್ನು ಸೇರಿಸಿ"
				: router.locale === "ar-AE"
					? "إضافة صور للعقار"
					: "Add property pictures",
		add_photos: router.locale === "kn-IN" ? "ಫೋಟೋಗಳನ್ನು ಸೇರಿಸಿ" : router.locale === "ar-AE" ? "أضف صور" : "Add photos",
		properties_with_more_than_5_pictues:
			router.locale === "kn-IN"
				? propertyData.property.is_ofb
					? "ಹೆಚ್ಚು ಚಿತ್ರಗಳನ್ನು ಹೊಂದಿರುವ OFB ಗಳು ಹೆಚ್ಚು ಪ್ರಶ್ನೆಗಳನ್ನು ಪಡೆಯುತ್ತವೆ"
					: "ಹೆಚ್ಚು ಚಿತ್ರಗಳನ್ನು ಹೊಂದಿರುವ ಆಸ್ತಿಗಳು ಹೆಚ್ಚು ಪ್ರಶ್ನೆಗಳನ್ನು ಪಡೆಯುತ್ತವೆ"
				: router.locale === "ar-AE"
					? propertyData.property.is_ofb
						? "أو إف بي التي تحتوي على أكثر من 5 صور تحصل على استفسارات أكثر"
						: "العقارات التي تحتوي على أكثر من 5 صور تحصل على استفسارات أكثر"
					: propertyData.property.is_ofb
						? "OFBs with more than 5 pictures gets more enquiries"
						: "Properties with more than 5 pictures gets more enquiries",
		noImages: router.locale === "kn-IN" ? "ಚಿತ್ರಗಳಿಲ್ಲ" : router.locale === "ar-AE" ? "لا توجد صور" : "No images",
		requestImages:
			router.locale === "kn-IN" ? "ಚಿತ್ರಗಳನ್ನು ಕೇಳಿ" : router.locale === "ar-AE" ? "اطلب الصور" : "Request Images",
	};

	/* Upload Picture modal */

	const handleUploadModalOpen = () => {
		setOpenProfileModal(true);
	};

	const handleUploadModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setOpenProfileModal(false);
		}
	};

	const handleEditProfileModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setEditProfileModal(false);
		}
	};

	const signIn = () => {
		if ((login_flow_global_state?.ui.type || "redirect") === "modal") {
			if (session && session.authorized) {
				new Function(...(login_flow_global_state?.action.sign_in || ["session", "return"])).call(session);
			} else {
				const { startFlow, resetFlow }: LoginFlowTriggers = createLoginFlow({
					dispatch,
					trigger_source: "navbar-dropdown",
					flow_type: "modal",
					autofill: false,
					action: {
						callback_url:
							process.env.PRODUCTION_API_URL +
							"auth/end-session?sessionId=" +
							encodeURIComponent(session?.session_id || "") +
							"&callbackUrl=" +
							encodeURIComponent(router.asPath),
						sign_in: (session: any) => {},
						sign_up: (session: any) => {},
						sign_out: () => {},
					},
				});
				startFlow();
			}
		} else {
		}
	};

	const propertyCreatedBy = (propertyData.property as LocalPropertyInterface).created_by;

	const submit = async (call_now: boolean) => {
		call_now ? null : setSnackOpen(true);

		const first_name = session ? (session.user ? (session.user.dbUser ? session.user.dbUser.first_name : "") : "") : "";
		const last_name = session ? (session.user ? (session.user.dbUser ? session.user.dbUser.last_name : "") : "") : "";
		const page_title = session
			? session.user
				? session.user.dbUser
					? session.user.dbUser.pages
						? session.user.dbUser.pages[0].page_title
						: ""
					: ""
				: ""
			: "";

		await dispatch(
			StoreLeadsThunk({
				name:
					page_title && page_title !== "" ? page_title : first_name && last_name ? first_name + " " + last_name : "",
				phone: {
					country_code: session
						? session.user
							? session.user.dbUser
								? session.user.dbUser.country_code
								: "+91"
							: "+91"
						: "+91",
					phone_number: session ? (session.user ? (session.user.dbUser ? session.user.dbUser.phone : "") : "") : "",
				},
				agreed_to_tc: true,
				source: "/properties/" + propertyData.property.slug,
				listing_slug: propertyData.property.slug,
				listing_type: transaction_type == "ofb" ? "ofb property" : "property",
				is_guest: session ? false : true,
				listing_title: propertyData.property.title,
				listing_id: propertyData.property._id,
				listing_owner: {
					is_business_profile: propertyCreatedBy.is_business_profile,
					user_id: propertyCreatedBy
						? propertyCreatedBy.is_business_profile
							? (propertyCreatedBy.user_id as string | ObjectId)
							: propertyCreatedBy.user_id !== ""
								? (propertyCreatedBy.user_id as UserInterface)._id
								: undefined
						: undefined,
					business_profile_id: propertyCreatedBy
						? propertyCreatedBy.is_business_profile
							? (propertyCreatedBy.business_profile_id as BusinessProfileInterface)._id
							: (propertyCreatedBy.business_profile_id as string | ObjectId)
						: undefined,
				},
				user_id: session ? (session.user ? (session.user.dbUser ? session.user.dbUser._id : "") : "") : "",
				is_lead_from_boost: propertyData.property.boost_information
					? propertyData.property.boost_information.active
					: false,
				created_by: {
					is_business_profile: profile_context.is_business_profile,
					user_id: profile_context.user_id as string | ObjectId,
					business_profile_id: profile_context.business_profile_id as string | ObjectId,
				},
			}),
		);
	};

	React.useEffect(() => {
		/* First let's define an event listener */

		const handleScroll = () => {
			if (window.scrollY < scrollY) {
				setNegativeScroll(true);
			} else {
				setNegativeScroll(false);
			}
			setScrollY(window.scrollY);
		};
		/* Now we can add the event listener */

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [scrollY]);

	return (
		<Container
			sx={{
				direction: router.locale === "ar-AE" ? "rtl" : "ltr",
			}}
		>
			<ContainerOne
				sx={{
					[theme.breakpoints.up("md1190")]: {
						// top: negativeScroll === true ? "4.5rem" : "0.5rem",
						// position: "sticky",
						// zIndex: "1",
					},
				}}
			>
				<RightContainer
					propertyData={propertyData.property as LocalPropertyInterface}
					property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
					session={session}
					redux_session_updated={redux_session_updated}
					dispatch={dispatch}
					isEditor={isEditor}
					loading={loading_property_images}
					profile_context={profile_context}
					creatorInnerRef={creatorInnerRef}
					login_flow_global_state={login_flow_global_state}
				/>
			</ContainerOne>
			{smDevice ? (
				<UploadImagesMobile
					open={openProfileModal}
					handleClose={handleUploadModalClose}
					propertyData={propertyData.property}
					session={session}
					index={0}
					dispatch={dispatch}
					isEditor={isEditor}
					profile_context={profile_context}
					property_snackbar={property_snackbar as PropertySnackbarInterface}
				/>
			) : (
				<UploadImageModal
					open={openProfileModal}
					handleClose={handleUploadModalClose}
					propertyData={propertyData.property}
					session={session}
					index={0}
					dispatch={dispatch}
					isEditor={isEditor}
					profile_context={profile_context}
					property_snackbar={property_snackbar as PropertySnackbarInterface}
				/>
			)}
			<ContainerTwo>
				<ImageContainer>
					{propertyData.property.images.length === 0 ? (
						<>
							{!loading_property_images ? (
								is_user_creator ||
								isEditor ||
								(logged_user_access.includes("admin") &&
									logged_user_access.includes("property") &&
									propertyData.property.assigned_to.includes(logged_user)) ||
								logged_user_access.includes("super_admin") ? (
									<ComponentContainer onClick={handleUploadModalOpen}>
										<AddPhotoAlternateOutlined sx={{ color: theme.palette.primary.main }} />

										<Typography
											sx={{
												fontSize: "14px",
												fontWeight: 500,
											}}
											variant="body1"
										>
											{localLang.add_property_pictures}
										</Typography>

										<Typography
											sx={{
												fontSize: "12px",
												fontWeight: 400,
												color: "#9E9E9E",
												textAlign: "center",
											}}
											variant="body1"
										>
											{localLang.properties_with_more_than_5_pictues}
										</Typography>

										<BeegruButton
											flavor="primary"
											variant="contained"
											size="small"
											onClick={handleUploadModalOpen}
											name="browse files"
											sx={{ margin: "0.25rem 0rem 0rem 0rem" }}
										>
											{localLang.add_photos}
										</BeegruButton>
									</ComponentContainer>
								) : (
									<ComponentContainer>
										{/* <DefaultImage src={process.env.CDN_URL + "images/uploads/property.jpg"} /> */}
										<SvgIcon
											component={RequestImagesIcon}
											inheritViewBox={false}
											viewBox="0 0 41 33"
											width={24}
											height={24}
											sx={{
												width: "2.563rem",
												height: "1.976rem",
												marginBottom: "0.5rem",
												fill: "none",
											}}
											// fill="none"
										/>
										<Typography
											variant="subtitle2"
											sx={{
												margin: "0rem 0rem 1.563rem 0rem",
												fontSize: "13px",
												fontWeight: 500,
												color: "#666666",
											}}
										>
											{localLang.noImages}
										</Typography>
										<BeegruButton
											style={{
												padding: "10px 20px",
												fontSize: "13px",
												fontWeight: 500,
												cursor: "pointer",
												backgroundColor: "#FC801929",
												color: "#B05911",
												borderRadius: "0.5rem",
												border: "none",
												width: "auto",
											}}
											onClick={(event: any) => {
												event.preventDefault();
												session !== null ? submit(false) : signIn();
											}}
										>
											{localLang.requestImages}
										</BeegruButton>
									</ComponentContainer>
								)
							) : (
								<LoadingContainer>
									<Skeleton
										variant="rectangular"
										animation="wave"
										sx={{
											width: "100%",
											height: "30rem",
											borderRadius: "16px",
											[theme.breakpoints.down("sm")]: {
												height: "15rem",
											},
										}}
									/>
								</LoadingContainer>
							)}
						</>
					) : (
						<NewPropertyImageComponent
							images={propertyData.property.images}
							loading={loading_property_images}
							propertyCreatedBy={(propertyData.property as LocalPropertyInterface).created_by}
							showContactDetails={propertyData.property.show_contact_details}
							session={session}
							propertyTitle={propertyData.property.title}
							propertySlug={propertyData.property.slug}
							dispatch={dispatch}
							isPropertyPublished={propertyData.property.published}
							isEditor={isEditor}
							profile_context={profile_context}
							transaction_type={propertyData.property.transaction_type}
							is_user_creator={is_user_creator}
							boost_information={propertyData.property.boost_information}
							ad_text={propertyData.property.ad_text}
							propertyId={propertyData.property._id}
							propertyLocation={propertyData.property.location.title}
							login_flow_global_state={login_flow_global_state}
						/>
					)}
				</ImageContainer>
				{/*
				{belowMd1190Device ? (
					<RightContainer
						propertyData={propertyData.property as LocalPropertyInterface}
						property_created_by={(propertyData.property as LocalPropertyInterface).created_by}
						session={session}
						redux_session_updated={redux_session_updated}
						dispatch={dispatch}
						isEditor={isEditor}
						loading={loading_property_creator}
						profile_context={profile_context}
						creatorInnerRef={creatorInnerRef}
					/>
				) : null} */}

				<LeftContainer
					propertyData={propertyData}
					dispatch={dispatch}
					session={session}
					location={location}
					profile_context={profile_context}
					saved_content={saved_content}
					negativeScroll={negativeScroll}
					isEditor={isEditor}
					loading={initialLoad}
					loading_property_details={loading_property_details}
					loading_property_config_and_area={loading_property_config_and_area}
					loading_about_property={loading_about_property}
					loading_overview={loading_overview}
					loading_amenity={loading_amenity}
					loading_about_developer={loading_about_developer}
					loading_about_location={loading_about_location}
					loading_reviews={loading_reviews}
					loading_vicinity={loading_vicinity}
					loading_neighbourhood_reviews={loading_neighbourhood_reviews}
					loading_map={loading_map}
					section_to_edit={section_to_edit}
					data_before_edit={data_before_edit}
					property_detailsInnerRef={property_detailsInnerRef}
					overviewInnerRef={overviewInnerRef}
					aboutPropertyInnerRef={aboutPropertyInnerRef}
					amenitiesInnerRef={amenitiesInnerRef}
					aboutDeveloperInnerRef={aboutDeveloperInnerRef}
					aboutLocationInnerRef={aboutLocationInnerRef}
					locationCoordinatesInnerRef={locationCoordinatesInnerRef}
					vicinityInnerRef={vicinityInnerRef}
					neighbourhoodReviewsInnerRef={neighbourhoodReviewsInnerRef}
					reviewsInnerRef={reviewsInnerRef}
					property_snackbar={property_snackbar}
					propertyDetails_inView={propertyDetails_inView}
					singlePropertyOverview_inView={singlePropertyOverview_inView}
					aboutSingleProperty_inView={aboutSingleProperty_inView}
					singlePropertyAmenities_inView={singlePropertyAmenities_inView}
					singlePropertyDeveloper_inView={singlePropertyDeveloper_inView}
					singlePropertyAboutLocation_inView={singlePropertyAboutLocation_inView}
					singlePropertyReviews_inView={singlePropertyReviews_inView}
					singlePropertyLocationCoordinates_inView={singlePropertyLocationCoordinates_inView}
					singlePropertyVicinity_inView={singlePropertyVicinity_inView}
					is_user_creator={is_user_creator}
					logged_user_access={logged_user_access}
					logged_user={logged_user}
					login_flow_global_state={login_flow_global_state}
				/>
			</ContainerTwo>
		</Container>
	);
};

export default SectionTwo;
